// MT: varyant ön-seçimsiz mod (MASTER-PLAN-v2 §2 "Beden ön-seçimi YOK", WP2.3).
// Yükleyen: blocks/variant-picker.liquid (MT-PATCH), yalnız "ön seçim yapma" açıkken.
//
// Davranış:
// - Adı listede olan seçeneklerin (ör. Beden) hiçbir değeri işaretli gelmez; başlıkta değer yazmaz ("Beden:" — QA tur 1
//   pdp-8), sticky çubukta varyant adı boş kalır.
// - Müşteri o seçeneği seçene kadar ana ATC ve sticky ATC'de fiyat yerine "[option] seçin" yazar (pdp-7). Butonlar
//   KAPATILMAZ (disabled değil): dokununca sayfa seçenek alanına kayar, alan vurgulanır, altında "[option] seçin"
//   uyarısı (role=alert) çıkar ve odak ilk seçilebilir değere geçer. Form gönderimini assets/product-form.js
//   (MT-PATCH isBlocked) engeller; bu modül aynı submit'te yönlendirmeyi yapar.
// - Seçim yapılınca etiket hemen "Sepete ekle"ye, başlık seçilen değere döner; Horizon akışı aynen çalışır (varyant
//   isteği + morph). Birden çok bekleyen seçenek varsa Horizon morph'u sunucunun seçtiği değeri geri işaretler; bu modül
//   bekleyenleri tekrar boşaltır.
// - URL'de ?variant= varsa (paylaşılan / geri dönülen bağlantı) hiçbir şey yapılmaz: müşterinin seçimi korunur.

const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class MtVariantGuard extends HTMLElement {
  connectedCallback() {
    if (new URLSearchParams(window.location.search).has('variant')) return;
    this.section = this.closest('.shopify-section') || document;
    const wanted = (this.dataset.options || '')
      .split(',')
      .map((name) => name.trim().toLocaleLowerCase())
      .filter(Boolean);
    /** @type {Set<string>} */
    this.pending = new Set();
    /** Bekçinin üzerine yazdığı metin düğümlerinin özgün metni (seçim yapılınca geri konur). */
    this.originals = new WeakMap();
    this.querySelectorAll('variant-picker fieldset input[data-option-name]').forEach((input) => {
      const name = /** @type {HTMLInputElement} */ (input).dataset.optionName || '';
      if (wanted.includes(name.toLocaleLowerCase())) this.pending.add(name);
    });
    if (!this.pending.size) return;

    this.section.addEventListener('change', this.onChange, true);
    this.section.addEventListener('submit', this.onSubmit, true);
    this.observer = new MutationObserver(this.queue);
    this.apply();
  }

  disconnectedCallback() {
    if (!this.section) return;
    this.section.removeEventListener('change', this.onChange, true);
    this.section.removeEventListener('submit', this.onSubmit, true);
    if (this.observer) this.observer.disconnect();
  }

  observe() {
    // Morph butonun metnini/özniteliğini geri yazabilir: metin ve işaret özniteliği de izlenir.
    this.observer.observe(this.section, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['data-mt-needs-option', 'disabled'],
    });
  }

  onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !this.contains(input)) return;
    const name = input.dataset.optionName;
    if (!name || !this.pending.has(name) || !input.checked) return;
    this.pending.delete(name);
    const fieldset = input.closest('fieldset');
    if (fieldset) {
      fieldset.removeAttribute('data-mt-pending');
      fieldset.removeAttribute('data-mt-attention');
      fieldset.querySelector('.mt-guard__hint')?.remove();
      // Başlık sunucu yanıtını beklemeden seçilen değeri göstersin ("Beden: L").
      const value = fieldset.querySelector('.mt-legend__value, .variant-option__swatch-value');
      if (value) value.textContent = input.value;
    }
    if (!this.pending.size) this.release();
  };

  onSubmit = (event) => {
    if (!this.pending.size) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.dataset.type !== 'add-to-cart-form') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.focusFirstPending();
  };

  queue = () => {
    if (this.queued || !this.pending.size) return;
    this.queued = true;
    requestAnimationFrame(() => {
      this.queued = false;
      this.apply();
    });
  };

  /** Dokunulan kapalı ATC: seçenek alanına götür, vurgula, uyarı yaz, odağı ilk seçilebilir değere ver. */
  focusFirstPending() {
    const fieldset = this.querySelector('fieldset[data-mt-pending]');
    if (!(fieldset instanceof HTMLElement)) return;
    fieldset.scrollIntoView({ block: 'center', behavior: reduced() ? 'auto' : 'smooth' });
    fieldset.setAttribute('data-mt-attention', '');
    const name = /** @type {HTMLInputElement | null} */ (fieldset.querySelector('input[data-option-name]'))?.dataset.optionName;
    let hint = fieldset.querySelector('.mt-guard__hint');
    if (!hint) {
      this.observer.disconnect();
      hint = document.createElement('p');
      hint.className = 'mt-guard__hint';
      hint.setAttribute('role', 'alert');
      fieldset.append(hint);
      this.observe();
    }
    hint.textContent = this.promptText(name);
    const first = fieldset.querySelector('input:not([aria-disabled="true"])') || fieldset.querySelector('input');
    if (first instanceof HTMLElement) first.focus({ preventScroll: true });
  }

  /** @param {string} [name] */
  promptText(name) {
    const option = name || this.pending.values().next().value || '';
    return (this.dataset.prompt || '[option]').replace('[option]', option);
  }

  buttons() {
    return [
      this.section.querySelector('product-form-component [ref="addToCartButton"]'),
      this.section.querySelector('.sticky-add-to-cart__button'),
    ].filter((button) => button instanceof HTMLButtonElement);
  }

  /**
   * Butondaki etiket düğümü: MT ATC (.mt-atc__label), sticky (.add-to-cart-text__content > span:first-child) ya da
   * Horizon'un doğrudan metni (Tükendi vb.).
   * @param {HTMLButtonElement} button
   */
  labelOf(button) {
    return (
      button.querySelector('.mt-atc__label, .add-to-cart-text__content > span:first-child') ||
      Array.from(button.childNodes).find((n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim()) ||
      null
    );
  }

  /** @param {Node | null} node @param {string} text */
  overwrite(node, text) {
    if (!node || node.textContent.trim() === text) return;
    this.originals.set(node, node.textContent);
    node.textContent = text;
  }

  apply() {
    if (!this.pending.size) return;
    this.observer.disconnect();
    this.querySelectorAll('variant-picker fieldset').forEach((fieldset) => {
      const inputs = Array.from(fieldset.querySelectorAll('input[data-option-name]'));
      const name = inputs[0] && /** @type {HTMLInputElement} */ (inputs[0]).dataset.optionName;
      if (!name || !this.pending.has(name)) return;
      fieldset.setAttribute('data-mt-pending', '');
      inputs.forEach((input) => {
        const radio = /** @type {HTMLInputElement} */ (input);
        radio.checked = false;
        radio.removeAttribute('checked');
        radio.dataset.currentChecked = 'false';
      });
      // pdp-8: seçim yokken başlıkta değer yazmasın (MT başlığı "Ad: değer" ve Horizon swatch değeri).
      fieldset.querySelectorAll('.variant-option__swatch-value, .mt-legend__value').forEach((value) => {
        if (value.textContent !== '') value.textContent = '';
      });
    });

    const text = this.promptText();
    this.buttons().forEach((button) => {
      // pdp-7: kapalı buton dokunuşu yutuyordu; buton açık kalır, gönderimi product-form.js engeller.
      if (button.disabled) button.disabled = false;
      if (button.getAttribute('aria-disabled') === 'true') button.removeAttribute('aria-disabled');
      button.setAttribute('data-mt-needs-option', '');
      this.overwrite(this.labelOf(button), text);
    });
    const variantTitle = this.section.querySelector('.sticky-add-to-cart__variant');
    if (variantTitle && variantTitle.textContent.trim() !== '') variantTitle.textContent = '';
    const express = this.section.querySelector('.accelerated-checkout-block');
    if (express) express.setAttribute('data-mt-hidden', '');
    this.observe();
  }

  /** Son bekleyen seçenek seçildi: etiket hemen geri gelir; Horizon'un varyant isteği butonları sunucu HTML'iyle yeniden çizer. */
  release() {
    this.observer.disconnect();
    this.buttons().forEach((button) => {
      button.removeAttribute('data-mt-needs-option');
      const label = this.labelOf(button);
      const original = label && this.originals.get(label);
      if (label && original != null && label.textContent.trim() !== original.trim()) label.textContent = original;
    });
    this.querySelectorAll('.mt-guard__hint').forEach((hint) => hint.remove());
    this.querySelectorAll('fieldset[data-mt-attention]').forEach((fieldset) => fieldset.removeAttribute('data-mt-attention'));
    const express = this.section.querySelector('.accelerated-checkout-block');
    if (express) express.removeAttribute('data-mt-hidden');
  }
}

if (!customElements.get('mt-variant-guard')) customElements.define('mt-variant-guard', MtVariantGuard);
